import { DeleteOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Col, Input, Row, Select } from "antd";
import { DrawerLink } from "components/UI/DrawerLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { PropertyCheckOperator, ResponsePropertySourceType } from "types/enums";
import { DrawerId } from "types/models";

import { FormData } from "../formSchema";

const { Option } = Select;

interface ConditionSectionProps {
    name: number; // index of the condition in the responseConnectionPropertiesCheck array
    methodIndex: number; // index of the method in methods array
    responseHandlerIndex: number; // index of the response handler in responseHandlers array
    handleRemove: () => void;
}

export const ConditionSection: FC<ConditionSectionProps> = ({
    name,
    methodIndex,
    responseHandlerIndex,
    handleRemove,
}) => {
    const { t } = usePaldockTranslation("ui.connection-creator-editor.response-condition");
    const reqSetting = DrawerId.ResponseConditionsSetting;
    const { control } = useFormContext<FormData>();

    const conditionPath =
        `methods.${methodIndex}.requestConfig.responseHandlers.${responseHandlerIndex}.responseConnectionPropertiesCheck.${name}` as const;

    const source = useWatch({
        control,
        name: `${conditionPath}.source`,
    }) as ResponsePropertySourceType;

    const operator = useWatch({
        control,
        name: `${conditionPath}.operator`,
    });

    const valueDisabled =
        operator === PropertyCheckOperator.IsNotEmpty ||
        operator === PropertyCheckOperator.IsEmpty ||
        operator === PropertyCheckOperator.IsTrue ||
        operator === PropertyCheckOperator.IsFalse ||
        operator === PropertyCheckOperator.Always;

    const showValue2 =
        operator === PropertyCheckOperator.IsBetweenThan || operator === PropertyCheckOperator.BetweenInclude;

    const accessorDisabled = source === ResponsePropertySourceType.HttpCode;

    return (
        <Row gutter={16} align="middle">
            <Col span={4}>
                <FormItem control={control} name={`${conditionPath}.source`} label={t("fields.source.label")} labelCol={{ span: 24 }}>
                    <Select placeholder={t("fields.source.placeholder")}>
                        {Object.values(ResponsePropertySourceType).map((source) => (
                            <Option value={source} key={source}>
                                {source}
                            </Option>
                        ))}
                    </Select>
                </FormItem>
            </Col>
            <Col span={4}>
                <FormItem
                    control={control}
                    name={`${conditionPath}.accessor`}
                    label={t("fields.accessor.label")}
                    labelCol={{ span: 24 }}
                    disabled={accessorDisabled}
                >
                    <Input placeholder={accessorDisabled ? "" : t("fields.accessor.placeholder")} />
                </FormItem>
            </Col>
            <Col span={4}>
                <FormItem control={control} name={`${conditionPath}.operator`} label={t("fields.operator.label")} labelCol={{ span: 24 }}>
                    <Select placeholder={t("fields.operator.placeholder")}>
                        {Object.values(PropertyCheckOperator).map((condition) => (
                            <Option value={condition} key={condition}>
                                {condition}
                            </Option>
                        ))}
                    </Select>
                </FormItem>
            </Col>
            <Col span={4}>
                <FormItem control={control} name={`${conditionPath}.value`} label={t("fields.value.label")} labelCol={{ span: 24 }}>
                    <Input placeholder={t("fields.value.placeholder")} disabled={valueDisabled} />
                </FormItem>
            </Col>
            {showValue2 && (
                <Col span={4}>
                    <FormItem
                        control={control}
                        name={`${conditionPath}.value2`}
                        label={t("fields.value2.label")}
                        labelCol={{ span: 24 }}
                    >
                        <Input placeholder={t("fields.value2.placeholder")} />
                    </FormItem>
                </Col>
            )}
            <Col>
                <Button danger onClick={handleRemove} icon={<DeleteOutlined />} />
            </Col>
            <Col>
                <DrawerLink
                    drawerId={reqSetting}
                    search={{
                        [`${reqSetting}-methodIndex`]: methodIndex.toString(),
                        [`${reqSetting}-responseHandlerIndex`]: responseHandlerIndex.toString(),
                        [`${reqSetting}-id`]: name.toString(),
                    }}
                    action="show"
                >
                    <Button icon={<SettingOutlined />} />
                </DrawerLink>
            </Col>
        </Row>
    );
};
